import { isEmailAddressValid, isPhoneNumberValid } from "@/helpers/strings";
import { Customer } from "@/types/customer";
import { StateCreator, create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";

export enum CustomerFormFields {
  FirstName,
  LastName,
  EmailAddress,
  PhoneNumber,
}

export interface PickupState extends Partial<Customer> {
  pickupStartAt?: string;
  setFirstName: (name: string) => void;
  setLastName: (name: string) => void;
  setEmailAddress: (address: string) => void;
  setPhoneNumber: (number: string) => void;
  setTimeSlot: (slot: string) => void;
  validateCustomer: () => boolean;
  getCustomer: () => Customer | null;
  reset: () => void;
  formFieldErrors: Record<keyof Customer, boolean>;
}

const initialStateCreator: StateCreator<PickupState> = (set, get, ...args) => ({
  firstName: undefined,
  lastName: undefined,
  emailAddress: undefined,
  phoneNumber: undefined,
  setFirstName: (firstName) =>
    set({
      firstName,
      formFieldErrors: {
        ...get().formFieldErrors,
        firstName: !firstName && get().formFieldErrors.firstName,
      },
    }),
  setLastName: (lastName) =>
    set({
      lastName,
      formFieldErrors: {
        ...get().formFieldErrors,
        lastName: !lastName && get().formFieldErrors.lastName,
      },
    }),
  setEmailAddress: (emailAddress) =>
    set({
      emailAddress,
      formFieldErrors: {
        ...get().formFieldErrors,
        emailAddress: !isEmailAddressValid(emailAddress) && get().formFieldErrors.emailAddress,
      },
    }),
  setPhoneNumber: (phoneNumber) =>
    set({
      phoneNumber,
      formFieldErrors: {
        ...get().formFieldErrors,
        phoneNumber: !isPhoneNumberValid(phoneNumber) && get().formFieldErrors.phoneNumber,
      },
    }),
  setTimeSlot: (pickupStartAt: string) => set({ pickupStartAt }),
  validateCustomer: () => {
    const errors: PickupState["formFieldErrors"] = {
      firstName: !get().firstName,
      lastName: !get().lastName,
      phoneNumber: !isPhoneNumberValid(get().phoneNumber ?? ""),
      emailAddress: !isEmailAddressValid(get().emailAddress ?? ""),
    };
    set({ formFieldErrors: errors });
    return Object.values(errors).every((e) => !e);
  },
  getCustomer: () => {
    const { firstName, lastName, phoneNumber, emailAddress } = get();
    if (!firstName || !lastName || !phoneNumber || !emailAddress || !get().validateCustomer())
      return null;
    return {
      firstName,
      lastName,
      phoneNumber,
      emailAddress,
    };
  },
  reset: () => set(initialStateCreator(set, get, ...args)),
  formFieldErrors: {
    firstName: false,
    lastName: false,
    emailAddress: false,
    phoneNumber: false,
  },
});

const usePickupStore = create(
  devtools(
    persist(initialStateCreator, {
      name: "pickup-storage",
      storage: createJSONStorage(() => sessionStorage),
    })
  )
);

export default usePickupStore;
